import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { EventType, Fleet } from '../interfaces';
import { FleetService } from '../services';
import { useImpersonatedUser } from './impersonation';

export const useAllFleetsEventTypesQuery = () => {
  const hasPermission = useHasPermission('events:read:resource');

  const queryKey = ['allFleetsEventType-key'];

  const queryFn = () => FleetService.getAllFleetsEventTypes();
  return useQuery({
    queryKey,
    queryFn,
    enabled: hasPermission,
    defaultData: [] as EventType[],
  });
};

export const useFleetEventTypesQuery = (fleetParam?: Fleet | null) => {
  const fleetFromContext = useFleet();
  // Only use context fleet when fleetParam is undefined (not passed)
  // When explicitly null, don't use any fleet (creation mode)
  const fleet = fleetParam !== undefined ? fleetParam : fleetFromContext;
  const hasPermission = useHasPermission('events:read:resource');
  const { data: impersonatedUser } = useImpersonatedUser();

  const fleetUuid = fleet?.uuid;

  // Identity belongs in the key: the response is scoped to whoever the request is authenticated
  // as (`X-Authenticated-As`), not just to the fleet. Without it, toggling impersonation leaves
  // the key identical — the fleet does not change — so React Query keeps serving the previous
  // user's event types as a valid cache hit, with `isLoading` false, until something invalidates
  // it. Keying on identity makes that a cache miss and a real loading state instead.
  // Normalised to `null` so "not impersonating" is always one stable key.
  const queryKey = ['fleetEventTypes-key', fleetUuid, impersonatedUser ?? null];

  const queryFn = () => FleetService.getFleetEventTypes(fleetUuid);

  return useQuery({
    queryKey,
    queryFn,
    enabled: hasPermission && !!fleetUuid,
    defaultData: [] as EventType[],
  });
};

export const forceRefreshEventTypes = (fleetUuid: Fleet['uuid']) => {
  queryClient.invalidateQueries(['fleetEventTypes-key', fleetUuid]);
};

export const forceRefreshAllFleetsEventTypes = () => {
  queryClient.invalidateQueries(['allFleetsEventType-key']);
};
